import { ReactNode } from "react";

import { FieldHint, ModalJsonContainer } from "../../shared";
import {
  SpecDetails,
  SpecSummary,
  SpecTable,
  SpecCode,
} from "./HospitalDetails.styles";

/**
 * Slugs of the Global condition catalogue. The server resolves `condition`
 * against the live `ConditionType` table (case-insensitively), but the select
 * endpoint (`GET /Retool/SelectValues/ConditionTypes`) exposes only id+name —
 * no slug — so the known catalogue is listed statically, the same way
 * Retool's modal did. A slug added by a seed migration still imports fine;
 * it just isn't listed here until this is updated.
 */
const CONDITION_SLUGS = [
  "asthma",
  "copd",
  "covid-19",
  "cystic-fibrosis",
  "dystonia",
  "haemodialysis",
  "heart-failure",
  "hypertension",
  "ipf",
  "kidney-transplant",
  "lung-transplant",
  "prostate-cancer",
  "type-1-diabetes",
  "vasculitis",
];

interface IColumnSpec {
  name: string;
  required: boolean;
  /** Server-side maximum length, or null where none applies. */
  maxLength: number | null;
  description: string;
  values: ReactNode;
}

const codeList = (values: string[]) =>
  values.map((value, i) => (
    <span key={value}>
      {i > 0 && ", "}
      <SpecCode>{value}</SpecCode>
    </span>
  ));

const COLUMNS: IColumnSpec[] = [
  {
    name: "email",
    required: true,
    maxLength: 254,
    description: "Patient email address — must not already be in use",
    values: "valid email",
  },
  {
    name: "first_name",
    required: true,
    maxLength: 30,
    description: "Patient first name",
    values: "free text",
  },
  {
    name: "last_name",
    required: true,
    maxLength: 30,
    description: "Patient last name",
    values: "free text",
  },
  {
    name: "condition",
    required: true,
    maxLength: null,
    description: "Condition slug (case-insensitive)",
    values: codeList(CONDITION_SLUGS),
  },
  {
    name: "hospital_patient_id",
    required: true,
    maxLength: 70,
    description: "Hospital/MRN identifier",
    values: "free text",
  },
  {
    name: "dob",
    required: true,
    maxLength: null,
    description: "Date of birth — cannot be in the future",
    values: <SpecCode>YYYY-MM-DD</SpecCode>,
  },
  {
    name: "sex",
    required: true,
    maxLength: 1,
    description: "Patient sex",
    values: codeList(["m", "f"]),
  },
  {
    name: "height_cm",
    required: false,
    maxLength: null,
    description: "Height in centimetres",
    values: "positive number",
  },
  {
    name: "ethnicity",
    required: false,
    maxLength: 16,
    description: "Patient ethnicity",
    // Retool's copy of this table also offered `hispanic`; the API has never
    // accepted it (AddPatientRequestValidator), so it is not listed.
    values: codeList(["white", "asian", "black", "other"]),
  },
];

const EXAMPLE = `email,first_name,last_name,condition,hospital_patient_id,dob,sex,height_cm,ethnicity
john.doe@example.com,John,Doe,ipf,MRN-001,1970-05-15,m,178,white
jane.smith@example.com,Jane,Smith,copd,MRN-002,1985-11-22,f,165,asian`;

/**
 * The CSV contract for a bulk patient import (EN-5175) — Retool's
 * "CSV structure details" modal. Folded into the import form as a collapsible
 * rather than a second modal: the portal shows one modal at a time, and
 * opening another would throw away the half-filled form.
 *
 * Every rule here mirrors what the server actually rejects
 * (`PatientsService.CreateBulkImportAsync` + `AddPatientRequestValidator`) —
 * this table is documentation, not enforcement.
 */
export function CsvStructureDetails() {
  return (
    <SpecDetails>
      <SpecSummary>CSV structure details</SpecSummary>
      <FieldHint>
        CSV with a header row. Max file size 1MB, max 500 rows. Column order
        does not matter and unknown columns are ignored. A file with two rows
        sharing an email is rejected outright.
      </FieldHint>
      <SpecTable>
        <thead>
          <tr>
            <th>Column</th>
            <th>Required</th>
            <th>Max length</th>
            <th>Description</th>
            <th>Values</th>
          </tr>
        </thead>
        <tbody>
          {COLUMNS.map((column) => (
            <tr key={column.name}>
              <td>
                <SpecCode>{column.name}</SpecCode>
              </td>
              <td>{column.required ? "Yes" : "No"}</td>
              <td>{column.maxLength ?? "—"}</td>
              <td>{column.description}</td>
              <td>{column.values}</td>
            </tr>
          ))}
        </tbody>
      </SpecTable>
      <FieldHint>Example:</FieldHint>
      <ModalJsonContainer>{EXAMPLE}</ModalJsonContainer>
    </SpecDetails>
  );
}
